import { Product } from "@domain/product/product";
import { ProductConfiguration } from "@domain/project/configurations/product-configuration";
import { ProjectEventType } from "@domain/project/project-event";
import type { ScopedProject } from "@domain/project/project-guards";
import { ProductItem } from "@project/product-item";
import { Expose, Transform, Type } from "class-transformer";
import { IsArray, IsString, ValidateNested } from "class-validator";
import { v4 as uuidv4 } from "uuid";

export class AlarmDeviceConfiguration extends ProductConfiguration {
  @Expose()
  @Transform(
    // migrate single productIds to array for backward compatibility
    (params) => (params.value?.length ? params.value : params.obj.productId ? [params.obj.productId] : []),
    { toClassOnly: true },
  )
  @IsArray()
  @IsString({ each: true })
  public productIds: string[];

  @Expose()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => ProductItem)
  @Transform(({ value, obj }) => {
    // migrate attachmentIds for backward compatibility
    if (Array.isArray(value)) return value;
    if (Array.isArray(obj.attachmentIds)) {
      return obj.attachmentIds.map((id: string) => ProductItem.create(id));
    }
    return [];
  })
  attachments: ProductItem[];

  constructor(id: string, name: string, project: ScopedProject, productIds: string[], attachments: ProductItem[]) {
    super(id, name, project);
    this.productIds = productIds;
    this.attachments = attachments;
  }

  static create(project: ScopedProject, alarmDevices: Product[], attachments: ProductItem[]): AlarmDeviceConfiguration {
    return new AlarmDeviceConfiguration(
      uuidv4(),
      AlarmDeviceConfiguration.buildName(alarmDevices),
      project,
      alarmDevices.map((alarmDevice) => alarmDevice.id),
      attachments,
    );
  }

  countPlacedProducts(project: ScopedProject = this.project): number {
    return project ? project.countPlacedAlarmDevices(this) : 0;
  }

  update(alarmDevices: Product[], attachments: ProductItem[]) {
    this.name = AlarmDeviceConfiguration.buildName(alarmDevices);
    this.productIds = alarmDevices.map((alarmDevice) => alarmDevice.id);
    this.attachments = attachments;
    this.project.publishUpdate(ProjectEventType.ALARM_DEVICE_CONFIGURATION_UPDATED, this);
  }

  override get mainProductId(): string {
    return this.productIds[0];
  }

  override get configuredProductIds(): string[] {
    return this.productIds.concat(this.attachmentIds);
  }

  get attachmentIds(): string[] {
    return this.attachments.map((attachment) => attachment.productId);
  }

  private static buildName(alarmDevices: Product[]) {
    if (alarmDevices.length === 1) {
      return alarmDevices[0].name;
    }
    return $localize`:@@alarmDeviceConfiguration.combination.name:Alarm-Kombi (${alarmDevices.map((alarmDevice) => alarmDevice.id).join(", ")}:listOfAlarmDeviceIds:)`;
  }
}
